import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PortalPaymentCard } from '@/modules/portal/components/PortalPaymentCard';
import { PortalQuickActionCard } from '@/modules/portal/components/ui/PortalQuickActionCard';
import { PortalPage } from '@/modules/portal/components/ui/PortalPage';
import { PortalLoadingState, portalSpinClass } from '@/modules/portal/components/ui/portal-loading';
import { PortalStagger } from '@/modules/portal/components/ui/portal-motion';
import { fetchPortalOverview } from '@/modules/portal/services/portalOverview.api';
import { fetchPortalPayments } from '@/modules/portal/services/portalPayments.api';
import { portalPaymentsShowRenewCta } from '@/modules/portal/utils/portalPaymentsHelpers';
import { Button, buttonVariants } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

export function PortalPaymentsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US';

  const {
    data: payments,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['portal', 'payments', 'list'],
    queryFn: fetchPortalPayments,
  });

  const { data: overview } = useQuery({
    queryKey: ['me', 'portal-overview'],
    queryFn: fetchPortalOverview,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <PortalLoadingState
        size="md"
        message={t('common.loading')}
        className="min-h-[280px] justify-center"
      />
    );
  }

  if (error) {
    return (
      <div className="portal-glass-panel mx-auto max-w-lg space-y-4 rounded-2xl border-destructive/30 p-6">
        <h2 className="text-lg font-semibold text-foreground">
          {t('portal.payments.error.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('portal.payments.error.message')}</p>
        <Button type="button" variant="outline" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const items = payments ?? [];
  const isEmpty = items.length === 0;
  const showRenewCta = portalPaymentsShowRenewCta(isEmpty, overview);

  return (
    <PortalPage
      title={t('portal.pages.payments.title')}
      description={t('portal.payments.description')}
      actions={
        isFetching && !isLoading ? (
          <Icons.RefreshCw
            className={cn('h-5 w-5 text-muted-foreground', portalSpinClass)}
            aria-hidden
          />
        ) : null
      }
    >
      {isEmpty ? (
        <div className="portal-empty-state portal-glass-panel max-w-lg space-y-4 rounded-2xl p-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted/80 text-muted-foreground">
            <Icons.DollarSign className="h-6 w-6" aria-hidden />
          </div>
          <h2 className="text-lg font-semibold text-foreground">
            {t('portal.payments.empty.title')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('portal.payments.empty.message')}</p>
          {showRenewCta ? (
            <Link
              to="/portal/renew"
              className={buttonVariants({ variant: 'default', className: 'w-full sm:w-auto' })}
            >
              {t('portal.payments.empty.renewCta')}
            </Link>
          ) : null}
        </div>
      ) : (
        <PortalStagger className="space-y-4">
          {items.map((payment) => (
            <PortalPaymentCard
              key={payment.id}
              payment={payment}
              {...(overview ? { overview } : {})}
              locale={locale}
            />
          ))}
        </PortalStagger>
      )}

      {!isEmpty && showRenewCta ? (
        <section className="space-y-3 pt-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('portal.payments.renewSection.title')}
          </h2>
          <PortalStagger className="grid gap-3 sm:grid-cols-2">
            <PortalQuickActionCard
              to="/portal/renew"
              label={t('portal.overview.actions.renew')}
              description={t('portal.payments.renewSection.hint')}
              icon={Icons.RefreshCw}
            />
          </PortalStagger>
        </section>
      ) : null}
    </PortalPage>
  );
}
